import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, MapPin, ArrowUp, ExternalLink, Building2 } from 'lucide-react';
import { companyInfo } from '../data/mock';

// Google Maps URL for the office location
const GOOGLE_MAPS_URL = "https://www.google.com/maps/search/?api=1&query=155+Dumdum+Road+Ghoshpara+Kolkata+700074";
// GeM Portal URL
const GEM_PORTAL_URL = "https://gem.gov.in";

const Footer = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Clients', href: '#clients' },
    { name: 'Contact', href: '#contact' }
  ];

  const services = [
    'Computer Sales & Service',
    'Server Maintenance',
    'Network Installation',
    'Software Supply',
    'Network Cabling',
    'Cisco Solutions',
    'WiFi Solutions',
    'AMC Services',
    'IT Facility Management',
    'Online UPS Solutions',
    'Battery Supply (SMF/Tubular)'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 15 }
    }
  };

  return (
    <footer className="footer" ref={ref}>
      <div className="footer-container">
        <motion.div 
          className="footer-content"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Company Info */}
          <motion.div className="footer-col company-col" variants={itemVariants}>
            <div className="footer-logo">
              <img 
                src="/PR%20NETCOM%20Transparent%20BG.png"
                alt="P.R. Net Com"
                className="footer-logo-img"
              />
            </div>
            <p className="footer-tagline">{companyInfo.tagline}</p>
            <p className="footer-description">
              ISO 9001:2008 certified IT solutions provider serving businesses across India since 2014.
            </p>
            <div className="footer-badges">
              <motion.div 
                className="footer-cert"
                whileHover={{ scale: 1.02 }}
              >
                <span>ISO 9001:2008 Certified</span>
              </motion.div>
              <motion.a 
                href={GEM_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-gem-badge"
                whileHover={{ scale: 1.02 }}
                data-testid="gem-badge"
              >
                <Building2 size={14} />
                <span>GeM Registered</span>
              </motion.a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div className="footer-col" variants={itemVariants}>
            <h4>Quick Links</h4>
            <ul className="footer-links">
              {quickLinks.map((link, i) => (
                <li key={link.name}>
                  <motion.a 
                    href={link.href}
                    whileHover={{ x: 5, color: '#3b82f6' }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div className="footer-col services-col" variants={itemVariants}>
            <h4>Our Services</h4>
            <ul className="footer-links footer-services-list">
              {services.map((service) => (
                <li key={service}>
                  <motion.span
                    whileHover={{ x: 3, color: '#3b82f6' }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    {service}
                  </motion.span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div className="footer-col contact-col" variants={itemVariants}>
            <h4>Contact Info</h4>
            <div className="contact-items">
              <motion.a 
                href={`mailto:${companyInfo.email}`} 
                className="contact-item"
                whileHover={{ x: 5, color: '#3b82f6' }}
                data-testid="footer-email"
              >
                <Mail size={18} />
                <span>{companyInfo.email}</span>
              </motion.a>
              {companyInfo.phones.map((phone, index) => (
                <motion.a 
                  key={index} 
                  href={`tel:${phone.replace(/\s/g, '')}`} 
                  className="contact-item"
                  whileHover={{ x: 5, color: '#3b82f6' }}
                  data-testid={`footer-phone-${index}`}
                >
                  <Phone size={18} />
                  <span>{phone}</span>
                </motion.a>
              ))}
              <motion.a 
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-item"
                whileHover={{ x: 5, color: '#3b82f6' }}
                data-testid="footer-visit-us"
              >
                <MapPin size={18} />
                <span>{companyInfo.address}</span>
                <ExternalLink size={12} style={{ marginLeft: '4px', opacity: 0.6 }} />
              </motion.a>
            </div>
          </motion.div>
        </motion.div>

        {/* Footer Bottom */}
        <motion.div 
          className="footer-bottom"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
        >
          <div className="footer-bottom-left">
            <p>&copy; {new Date().getFullYear()} <span className="footer-brand">P.R. NET COM</span>. All rights reserved.</p>
          </div>
          <div className="footer-bottom-right">
            <p className="gst-info">GST: {companyInfo.gst}</p>
            <p className="gem-info" data-testid="gem-id-display">
              <Building2 size={14} />
              <span>GeM ID: {companyInfo.gemId}</span>
            </p>
          </div>
        </motion.div>
      </div>

      {/* Scroll to Top */}
      <motion.button 
        className="scroll-top-btn" 
        onClick={scrollToTop} 
        type="button"
        whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(59, 130, 246, 0.4)' }}
        whileTap={{ scale: 0.95 }}
        data-testid="scroll-top-btn"
      >
        <ArrowUp size={24} />
      </motion.button>
    </footer>
  );
};

export default Footer;
